<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { frontendURL } from 'dashboard/helper/URLHelper';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TasksAPI from 'kanban/api/tasks';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { localeTag } = useDateLocale();

const tasks = ref([]);
const isLoading = ref(false);

const fetchTasks = async () => {
  if (!route.params.contactId) return;
  isLoading.value = true;
  try {
    const { data } = await TasksAPI.get({
      contact_id: route.params.contactId,
      per_page: 5,
    });
    tasks.value = data?.tasks || [];
  } catch (error) {
    tasks.value = [];
  } finally {
    isLoading.value = false;
  }
};

const openTask = task =>
  router.push(
    frontendURL(
      `accounts/${route.params.accountId}/kanban/${task.board_id}/task/${task.id}`
    )
  );

const formatDate = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleDateString(localeTag.value);
};

const isOverdue = task => {
  if (!task.due_date) return false;
  if (task.date_status) return task.date_status === 'overdue';
  return new Date(task.due_date) < new Date();
};

onMounted(fetchTasks);
</script>

<template>
  <div class="flex flex-col">
    <div v-if="isLoading" class="flex justify-center py-4 text-n-slate-11">
      <Spinner :size="20" />
    </div>
    <div v-else-if="tasks.length" class="flex flex-col gap-2">
      <div
        v-for="task in tasks"
        :key="task.id"
        class="flex items-center justify-between gap-3 p-3 transition-colors border rounded-lg cursor-pointer border-n-weak hover:bg-n-alpha-1"
        @click="openTask(task)"
      >
        <div class="flex flex-col min-w-0">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ task.title }}
          </span>
          <span
            v-if="task.board?.name"
            class="text-xs truncate text-n-slate-11"
          >
            {{ task.board.name }}
          </span>
        </div>
        <div class="flex items-center gap-3 text-xs shrink-0 text-n-slate-11">
          <div
            v-if="task.assigned_agents?.length"
            class="flex items-center ltr:-space-x-1.5 rtl:-space-x-reverse"
          >
            <Avatar
              v-for="agent in task.assigned_agents.slice(0, 3)"
              :key="agent.id"
              :name="agent.name"
              :src="agent.avatar_url"
              :size="18"
              rounded-full
            />
          </div>
          <div
            v-if="task.due_date"
            class="flex items-center gap-1"
            :class="isOverdue(task) ? 'text-n-ruby-11' : ''"
          >
            <Icon icon="i-lucide-calendar" class="size-3.5" />
            <span>{{ formatDate(task.due_date) }}</span>
          </div>
        </div>
      </div>
    </div>
    <p v-else class="py-2 text-sm text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.OVERVIEW.TASKS_EMPTY') }}
    </p>
  </div>
</template>
